// Écran d'entrée du taskpane : vérification d'identité Microsoft + licence Praetor, et les écrans
// d'erreur/CGU associés. onSuccess (affichage de l'app, showAppBody dans outlook.ts) est injecté
// pour éviter une dépendance circulaire avec le bootstrap.
import { acquireGraphToken, getAccountEmail } from "../graph";
import { mailData } from "../mailData";
import { acceptCgu, checkLicense, LicenseCheckResult } from "../praetoria";
import { CGU_VERSION } from "../config";
import { describeActionError } from "../ui/domHelpers";

interface Identity {
  email: string;
  graphToken?: string;
}

async function resolveIdentity(): Promise<Identity> {
  const officeEmail = Office.context.mailbox.userProfile ? Office.context.mailbox.userProfile.emailAddress : "";

  if (!Office.context.requirements.isSetSupported("NestedAppAuth", "1.1")) {
    return { email: officeEmail };
  }

  const graphToken = await acquireGraphToken();
  if (!graphToken) {
    return { email: officeEmail };
  }

  return { email: (await getAccountEmail()) || officeEmail, graphToken };
}

function showLicenseError(email: string): void {
  document.getElementById("identity-error").style.display = "none";
  document.getElementById("technical-error").style.display = "none";
  document.getElementById("cgu-required").style.display = "none";
  document.getElementById("service-unavailable").style.display = "none";
  document.getElementById("license-error-email").textContent = email;
  document.getElementById("license-error").style.display = "flex";
}

function showIdentityError(email: string): void {
  document.getElementById("license-error").style.display = "none";
  document.getElementById("technical-error").style.display = "none";
  document.getElementById("cgu-required").style.display = "none";
  document.getElementById("service-unavailable").style.display = "none";
  document.getElementById("identity-error-email").textContent = email;
  document.getElementById("identity-error").style.display = "flex";
}

function showTechnicalError(): void {
  document.getElementById("license-error").style.display = "none";
  document.getElementById("identity-error").style.display = "none";
  document.getElementById("cgu-required").style.display = "none";
  document.getElementById("service-unavailable").style.display = "none";
  document.getElementById("technical-error").style.display = "flex";
}

// Panne de base côté serveur (503, code=indisponibilite_technique -- voir main.py) : écran DÉDIÉ,
// distinct de "technical-error", pour ne jamais laisser croire à une erreur définitive nécessitant
// de contacter le support alors que le service se rétablit de lui-même dès que la base répond.
function showServiceUnavailable(): void {
  document.getElementById("license-error").style.display = "none";
  document.getElementById("identity-error").style.display = "none";
  document.getElementById("cgu-required").style.display = "none";
  document.getElementById("technical-error").style.display = "none";
  document.getElementById("service-unavailable").style.display = "flex";
}

function showCguRequired(): void {
  document.getElementById("license-error").style.display = "none";
  document.getElementById("identity-error").style.display = "none";
  document.getElementById("technical-error").style.display = "none";
  document.getElementById("service-unavailable").style.display = "none";
  const checkbox = document.getElementById("cgu-required-checkbox") as HTMLInputElement;
  checkbox.checked = false;
  const acceptButton = document.getElementById("cgu-required-accept");
  acceptButton.classList.add("is-disabled");
  acceptButton.setAttribute("aria-disabled", "true");
  const statusElement = document.getElementById("cgu-required-status");
  statusElement.textContent = "";
  statusElement.className = "reply-status";
  document.getElementById("cgu-required").style.display = "flex";
}

export async function runLicenseCheck(onSuccess: () => void): Promise<void> {
  const { email, graphToken } = await resolveIdentity();
  mailData.userEmail = email;
  mailData.graphToken = graphToken;

  if (!graphToken) {
    showIdentityError(email);
    return;
  }

  const result: LicenseCheckResult = await checkLicense(graphToken);
  if (result === "identity_error") {
    showIdentityError(email);
    return;
  }
  if (result === "invalid_license") {
    showLicenseError(email);
    return;
  }
  if (result === "cgu_required") {
    showCguRequired();
    return;
  }
  if (result === "service_unavailable") {
    showServiceUnavailable();
    return;
  }
  if (result === "technical_error") {
    showTechnicalError();
    return;
  }
  onSuccess();
}

const MIN_LICENSE_RETRY_LOADING_MS = 1500;

export async function handleAuthRetry(onSuccess: () => void): Promise<void> {
  const labelElements = [
    document.getElementById("license-retry-label"),
    document.getElementById("identity-retry-label"),
    document.getElementById("technical-retry-label"),
    document.getElementById("service-unavailable-retry-label"),
  ];
  const loadingElements = [
    document.getElementById("license-retry-loading"),
    document.getElementById("identity-retry-loading"),
    document.getElementById("technical-retry-loading"),
    document.getElementById("service-unavailable-retry-loading"),
  ];
  labelElements.forEach((element) => element.classList.add("is-hidden"));
  loadingElements.forEach((element) => element.classList.remove("is-hidden"));

  const minDelay = new Promise((resolve) => setTimeout(resolve, MIN_LICENSE_RETRY_LOADING_MS));
  await Promise.all([runLicenseCheck(onSuccess), minDelay]);

  labelElements.forEach((element) => element.classList.remove("is-hidden"));
  loadingElements.forEach((element) => element.classList.add("is-hidden"));
}

let isAcceptingCgu = false;

export function handleCguCheckboxChange(): void {
  const checkbox = document.getElementById("cgu-required-checkbox") as HTMLInputElement;
  const acceptButton = document.getElementById("cgu-required-accept");
  acceptButton.classList.toggle("is-disabled", !checkbox.checked);
  if (checkbox.checked) {
    acceptButton.removeAttribute("aria-disabled");
  } else {
    acceptButton.setAttribute("aria-disabled", "true");
  }
}

export async function handleCguAccept(onSuccess: () => void): Promise<void> {
  const checkbox = document.getElementById("cgu-required-checkbox") as HTMLInputElement;
  const graphToken = mailData.graphToken;
  if (isAcceptingCgu || !checkbox.checked || !graphToken) {
    return;
  }

  const acceptLabel = document.getElementById("cgu-required-accept-label");
  const acceptLoading = document.getElementById("cgu-required-accept-loading");
  const statusElement = document.getElementById("cgu-required-status");
  isAcceptingCgu = true;
  acceptLabel.classList.add("is-hidden");
  acceptLoading.classList.remove("is-hidden");
  statusElement.textContent = "";
  statusElement.className = "reply-status";

  try {
    await acceptCgu(graphToken, CGU_VERSION);
    // Relance la vérification complète : le serveur délivre maintenant le JWT (les CGU
    // courantes sont acceptées), au même titre qu'un "Réessayer" après correction d'une
    // autre erreur (handleAuthRetry).
    await runLicenseCheck(onSuccess);
  } catch (error) {
    statusElement.textContent = describeActionError("Impossible d'enregistrer votre acceptation", error);
    statusElement.className = "reply-status reply-status--error";
  } finally {
    isAcceptingCgu = false;
    acceptLabel.classList.remove("is-hidden");
    acceptLoading.classList.add("is-hidden");
  }
}
